import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Checkbox,
  Label,
  Textarea,
  useToast,
  Switch,
  Input,
} from '@org/ui';
import { AlertTriangle, Trash2, Ban, CheckCircle, XCircle } from 'lucide-react';

type ReportStatus = 'pending' | 'reviewed' | 'resolved' | 'dismissed';
type TargetType = 'post' | 'comment' | 'message' | 'user';

interface Report {
  reportId: string;
  targetType: TargetType;
  targetId: string;
  status: ReportStatus;
  target?: any;
}

interface ResolveReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: Report;
  onSuccess?: () => void;
}

export function ResolveReportModal({
  isOpen,
  onClose,
  report,
  onSuccess,
}: ResolveReportModalProps) {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [resolution, setResolution] = useState<'resolved' | 'dismissed'>('resolved');
  const [moderatorNotes, setModeratorNotes] = useState('');
  const [deleteContent, setDeleteContent] = useState(false);
  const [banUser, setBanUser] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banDays, setBanDays] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const resolveReport = communityTrpc.moderation.resolveReport.useMutation({
    onSuccess: () => {
      toast({ title: t('admin.resolveModal.resolvedSuccess') });
      resetForm();
      onSuccess?.();
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setResolution('resolved');
    setModeratorNotes('');
    setDeleteContent(false);
    setBanUser(false);
    setBanReason('');
    setBanDays(null);
    setConfirmDelete(false);
  };

  const handleSubmit = () => {
    if (deleteContent && !confirmDelete) {
      toast({
        title: t('admin.resolveModal.confirmRequired'),
        description: t('admin.resolveModal.confirmDeleteDesc'),
        variant: 'destructive',
      });
      return;
    }

    if (banUser && !banReason.trim()) {
      toast({
        title: t('admin.resolveModal.banReasonRequired'),
        description: t('admin.resolveModal.banReasonRequiredDesc'),
        variant: 'destructive',
      });
      return;
    }

    const banExpiresAt = banDays
      ? new Date(Date.now() + banDays * 24 * 60 * 60 * 1000).toISOString()
      : undefined;

    resolveReport.mutate({
      reportId: report.reportId,
      status: resolution,
      moderatorNotes: moderatorNotes || undefined,
      deleteContent,
      banUser,
      banReason: banUser ? banReason : undefined,
      banExpiresAt,
    });
  };

  const canDeleteContent = report.targetType !== 'user';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('admin.resolveModal.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.resolveModal.description', { type: report.targetType })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Resolution Type */}
          <div className="space-y-3">
            <Label>{t('admin.resolveModal.resolutionLabel')}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setResolution('resolved')}
                className={`h-auto gap-2 rounded-lg p-3 ${
                  resolution === 'resolved'
                    ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-50 hover:text-green-700'
                    : 'hover:bg-muted/50'
                }`}
              >
                <CheckCircle className="h-4 w-4" />
                {t('admin.resolveModal.resolve')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setResolution('dismissed')}
                className={`h-auto gap-2 rounded-lg p-3 ${
                  resolution === 'dismissed'
                    ? 'border-muted-foreground bg-muted/50 text-muted-foreground hover:bg-muted/50 hover:text-muted-foreground'
                    : 'hover:bg-muted/50'
                }`}
              >
                <XCircle className="h-4 w-4" />
                {t('admin.resolveModal.dismiss')}
              </Button>
            </div>
          </div>

          {/* Moderator Notes */}
          <div className="space-y-2">
            <Label htmlFor="moderatorNotes">{t('admin.resolveModal.moderatorNotesLabel')}</Label>
            <Textarea
              id="moderatorNotes"
              value={moderatorNotes}
              onChange={(e) => setModeratorNotes(e.target.value)}
              placeholder={t('admin.resolveModal.moderatorNotesPlaceholder')}
              rows={3}
            />
          </div>

          {/* Actions Section */}
          {resolution === 'resolved' && (
            <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">{t('admin.resolveModal.moderationActions')}</span>
              </div>

              {/* Delete Content */}
              {canDeleteContent && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-red-600" />
                      <Label htmlFor="deleteContent" className="cursor-pointer">
                        {t('admin.resolveModal.deleteTarget', { type: report.targetType })}
                      </Label>
                    </div>
                    <Switch
                      id="deleteContent"
                      checked={deleteContent}
                      onCheckedChange={setDeleteContent}
                    />
                  </div>

                  {deleteContent && (
                    <div className="ml-6 flex items-center gap-2">
                      <Checkbox
                        id="confirmDelete"
                        checked={confirmDelete}
                        onCheckedChange={(checked) => setConfirmDelete(checked === true)}
                        className="border-border"
                      />
                      <Label
                        htmlFor="confirmDelete"
                        className="text-sm text-red-600"
                      >
                        {t('admin.resolveModal.confirmDeleteLabel')}
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {/* Ban User */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban className="h-4 w-4 text-red-600" />
                    <Label htmlFor="banUser" className="cursor-pointer">
                      {t('admin.resolveModal.banUserLabel')}
                    </Label>
                  </div>
                  <Switch
                    id="banUser"
                    checked={banUser}
                    onCheckedChange={setBanUser}
                  />
                </div>

                {banUser && (
                  <div className="ml-6 space-y-3">
                    <div>
                      <Label htmlFor="banReason" className="text-xs">
                        {t('admin.resolveModal.banReasonLabel')}
                      </Label>
                      <Input
                        id="banReason"
                        value={banReason}
                        onChange={(e) => setBanReason(e.target.value)}
                        placeholder={t('admin.resolveModal.banReasonPlaceholder')}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="banDays" className="text-xs">
                        {t('admin.resolveModal.banDurationLabel')}
                      </Label>
                      <Input
                        id="banDays"
                        type="number"
                        min={1}
                        value={banDays ?? ''}
                        onChange={(e) =>
                          setBanDays(e.target.value ? parseInt(e.target.value) : null)
                        }
                        placeholder={t('admin.resolveModal.permanentBan')}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={resolveReport.isPending}
            variant={resolution === 'resolved' ? 'default' : 'secondary'}
          >
            {resolveReport.isPending
              ? t('admin.bans.processing')
              : resolution === 'resolved'
                ? t('admin.resolveModal.resolveReportBtn')
                : t('admin.resolveModal.dismissReportBtn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
